import { useCallback, useMemo, useState } from 'react'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMenuKeyboardShortcuts } from 'govwell-ui/components/Menu/use-menu-keyboard-shortcuts'
import isNil from 'lodash.isnil'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import styled, { css } from 'styled-components'

import { FilterManager } from 'src/models/TableViews/FilterManager'
import { TableColumnTemplate } from 'src/models/TableViews/TableColumnTemplate'
import { KeyboardKey } from 'src/types'

import { Popover } from '../../govwell-ui'
import { MenuContentStyles } from '../../govwell-ui/components/Menu'
import useDisclosure from '../../hooks/use-disclosure'
import Text, { TextSize, getFontSize } from '../Typography/Text'

const ActiveStyles = css`
  background-color: ${({ theme }) => theme.controlItemBgHover};
`
const StyledMenuItem = styled.div<{ $isActive: boolean }>`
  cursor: pointer;
  padding: 9px 12px;
  border-radius: 4px;
  font-size: ${getFontSize(TextSize.Base)}px;
  ${({ $isActive }) => $isActive && ActiveStyles};
`

const StyledFilterText = styled(Text)`
  cursor: pointer;
  padding: 0px 3px;
  border-radius: 6px;
  &:hover {
    background-color: ${({ theme }) => theme.controlItemBgHover};
  }
  &:focus-visible {
    outline: solid 2px ${({ theme }) => theme.colorPrimaryBorder};
  }
`

const StyledContent = styled(Popover.Content)`
  ${MenuContentStyles}
  &:focus {
    outline: none;
  }
`

type FilterOption = {
  id: `filter-type-${string}`
  label: string
  onClick: () => void
}

type Props = {
  filterManager: FilterManager
  columnTemplate: TableColumnTemplate
}
const FilterTypeSelect = ({ filterManager, columnTemplate }: Props) => {
  const filter = filterManager.getFilterByColumnGeneratedId(columnTemplate.generatedId)
  const {
    isOpen: isMenuOpen,
    open: onOpenMenu,
    close: onCloseMenu,
    toggle: onToggleMenu,
  } = useDisclosure()

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === KeyboardKey.Enter || e.key === KeyboardKey.Space) {
        onToggleMenu()
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [onToggleMenu]
  )

  const handleIsInClicked = useCallback(() => {
    runInAction(() => {
      filter.isNull = undefined
      filter.membershipType = 'IsIn'
      onCloseMenu()
    })
  }, [filter, onCloseMenu])

  const handleIsNotInClicked = useCallback(() => {
    runInAction(() => {
      filter.isNull = undefined
      filter.membershipType = 'IsNotIn'
      onCloseMenu()
    })
  }, [filter, onCloseMenu])

  const handleIsEmptyClicked = useCallback(() => {
    runInAction(() => {
      filter.isNull = true
      filter.optionsSelected = []
      onCloseMenu()
    })
  }, [filter, onCloseMenu])

  const handleIsNotEmptyClicked = useCallback(() => {
    runInAction(() => {
      filter.isNull = false
      filter.optionsSelected = []
      onCloseMenu()
    })
  }, [filter, onCloseMenu])

  const isInFilterOption: FilterOption = useMemo(
    () => ({
      id: 'filter-type-is-in',
      label: 'Is',
      onClick: handleIsInClicked,
    }),
    [handleIsInClicked]
  )
  const isNotInFilterOption: FilterOption = useMemo(
    () => ({
      id: 'filter-type-is-not-in',
      label: 'Is not',
      onClick: handleIsNotInClicked,
    }),
    [handleIsNotInClicked]
  )
  const isEmptyFilterOption: FilterOption = useMemo(
    () => ({
      id: 'filter-type-is-empty',
      label: 'Is empty',
      onClick: handleIsEmptyClicked,
    }),
    [handleIsEmptyClicked]
  )
  const isNotEmptyFilterOption: FilterOption = useMemo(
    () => ({
      id: 'filter-type-is-not-empty',
      label: 'Is not empty',
      onClick: handleIsNotEmptyClicked,
    }),
    [handleIsNotEmptyClicked]
  )
  const options = useMemo(() => {
    // Show "Is" and "Is not" options when:
    // 1. There are more than 2 options (subset selection makes sense), OR
    // 2. The column is nullable with at least 2 options (because "Is not X" differs from "Is Y" when null values exist)
    const showIsInOptions =
      columnTemplate.filterOptions.length > 2 ||
      (columnTemplate.filterOptions.length >= 2 && columnTemplate.isNullable)
    return [
      ...(showIsInOptions ? [isInFilterOption, isNotInFilterOption] : []),
      ...(columnTemplate.isNullable ? [isEmptyFilterOption, isNotEmptyFilterOption] : []),
    ]
  }, [
    columnTemplate.filterOptions.length,
    columnTemplate.isNullable,
    isEmptyFilterOption,
    isInFilterOption,
    isNotEmptyFilterOption,
    isNotInFilterOption,
  ])

  const defaultActiveIndex = useMemo(() => {
    let targetId: FilterOption['id']
    if (!isNil(filter.isNull)) {
      targetId = filter.isNull ? 'filter-type-is-empty' : 'filter-type-is-not-empty'
    } else {
      targetId =
        (filter.membershipType ?? 'IsIn') === 'IsIn' ? 'filter-type-is-in' : 'filter-type-is-not-in'
    }
    const index = options.findIndex((opt) => opt.id === targetId)
    // Fall back to 0 if the target option isn't in the current options array
    return index >= 0 ? index : 0
  }, [filter.isNull, filter.membershipType, options])

  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex)
  const { menuItemRefs } = useMenuKeyboardShortcuts({
    activeIndex,
    count: options.length,
    isEnabled: isMenuOpen,
    onEscape: onCloseMenu,
    onSelect: () => {
      options[activeIndex]?.onClick()
    },
    setActiveIndex,
  })

  const currentFilterText = useMemo(() => {
    if (!isNil(filter.isNull)) {
      return filter.isNull ? 'is empty' : 'is not empty'
    }
    return (filter.membershipType ?? 'IsIn') === 'IsIn' ? 'is' : 'is not'
  }, [filter.isNull, filter.membershipType])

  if (options.length === 0) {
    return null
  }

  return (
    <Popover isOpen={isMenuOpen} onClose={onCloseMenu} onOpen={onOpenMenu}>
      <Popover.Trigger asChild>
        <StyledFilterText role="button" tabIndex={0} onKeyDown={handleKeyDown}>
          {currentFilterText}&nbsp;
          <FontAwesomeIcon icon={isMenuOpen ? faChevronUp : faChevronDown} />
        </StyledFilterText>
      </Popover.Trigger>
      <StyledContent
        sideOffset={0}
        role="menu"
        tabIndex={0}
        aria-activedescendant={options[activeIndex]?.id}
      >
        {options.map((o, index) => (
          <StyledMenuItem
            role="menuitem"
            onClick={o.onClick}
            $isActive={activeIndex === index}
            onMouseMove={() => setActiveIndex(index)}
            tabIndex={-1}
            ref={(el) => menuItemRefs.current.set(index, el)}
            id={o.id}
            key={o.id}
          >
            {o.label}
          </StyledMenuItem>
        ))}
      </StyledContent>
    </Popover>
  )
}

export default observer(FilterTypeSelect)
